import React, { useState, useEffect, useRef } from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import compose from 'recompose/compose'
import _isNil from 'lodash/isNil'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { getBoundActions } from '../../actions/index'
import { getUserLanguagePrimary } from '../../services/user/index'
import { EN } from '../../services/languages/constants'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import Button, { TYPES } from '../Button/index'
import HeroImage, { HERO_IMAGE_OVERLAY_OPACITY_MEDIUM } from '../HeroImage/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import { H2 } from '../Heading/index'
import {
  PROMO_UNIT_CLICK_GA4_EVENT,
  PROMO_UNIT_IMPRESSION_GA4_EVENT,
} from '../../services/event-tracking/index'
import { useOnScreen } from '../../services/pm-screen/util/index'
import { selectPromotions, getSafeNodeId } from '../../services/node/index'
import {
  TYPE_CONTENT_PROMOTION,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_SERIES,
} from '../../services/content-type/index'
import { get as getConfig } from '../../config/index'
import { createUpstreamContext, SCREEN_TYPE_MEMBER_HOME } from '../../services/upstream-context/index'
import { createRelative, isVideoOrSeries as isVideoOrSeriesPath } from '../../services/url/index'
import { WINBACK_BANNER, isWinbackFlagEnabled } from '../../services/winback/index'
import PromotionWinback from './PromotionWinback'

const config = getConfig()
const promotionGraphQLActive = !!_get(config, ['features', 'promotionGraphQL'])

/**
 * The background image for the promotion
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 * @param {String} props.promotionContentType - The promotion content type
 * @returns {JSX.Element} - The background image for the promotion
 */
function Background (props) {
  const { data, promotionContentType } = props
  const background = data.getIn(['heroImage'], null)
  const smallUrl = !_isNil(background) && background.getIn(['small'], null)
  const mediumSmallUrl = !_isNil(background) && background.getIn(['mediumSmall'], null)
  const mediumUrl = !_isNil(background) && background.getIn(['medium'], null)
  const largeUrl = !_isNil(background) && background.getIn(['large'], null)

  return (
    <div className="promotion__background">
      <HeroImage
        className={['background']}
        hasOverlay={promotionContentType !== TYPE_CONTENT_PROMOTION}
        overlayOpacity={HERO_IMAGE_OVERLAY_OPACITY_MEDIUM}
        smallUrl={smallUrl}
        mediumSmallUrl={mediumSmallUrl}
        mediumUrl={mediumUrl}
        largeUrl={largeUrl}
        url={largeUrl}
      />
    </div>
  )
}

/**
 * The logo image for the promotion
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 * @returns {JSX.Element} - The logo image for the promotion
 */
function Logo (props) {
  const { data } = props
  const title = data.get('title')
  let logo = data.get('seriesLogo', null)

  if (promotionGraphQLActive) {
    logo = data.getIn(['logo_image', 'logo_xl'])
  }

  return (
    !logo
      ? <H2 className="promotion__title">
          {title}
        </H2>
      : <img
          src={logo}
          alt={title}
          className="promotion__logo"
        />
  )
}

/**
 * The short description for the promotion
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 * @returns {JSX.Element} - The short description for the promotion
 */
function Description (props) {
  const { data } = props
  const description = data.get('teaser', '')

  return (
    <p className="promotion__description">
      {description}
    </p>
  )
}

/**
 * The cta button for the promotion
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 * @param {String} props.promotionContentType - The promotion content type
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} - The cta button for the promotion
 */
function ButtonCTA (props) {
  const {
    data,
    promotionContentType,
    staticText,
  } = props

  const isPromotionType = promotionContentType === TYPE_CONTENT_PROMOTION
  const ctaButton = data.getIn(['cta'])
  const ctaText = ctaButton.getIn(['label'])
  const ctaLink = ctaButton.getIn(['path'])

  if (isPromotionType && !ctaText) {
    return null
  }

  let isVideoOrSeries = [TYPE_CONTENT_SERIES, TYPE_CONTENT_VIDEO].includes(promotionContentType)
  let url = data.get('path') || data.get('url')
  let label = staticText.get('seriesVideoCTA')

  if (isPromotionType) {
    const relativeCtaLink = createRelative(ctaLink)
    isVideoOrSeries = isVideoOrSeriesPath(relativeCtaLink)
    url = ctaLink
    label = ctaText
  }

  return (
    <Button
      className="promotion__cta-button"
      type={TYPES.PRIMARY}
      url={url}
      scrollToTop
      onClick={() => handleClick(data, props, isVideoOrSeries)}
    >
      {label}
    </Button>
  )
}

/**
 * The visual interest image for the promotion
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 * @param {String} props.promotionContentType - The promotion content type
 * @returns {JSX.Element} - The visual interest image for the promotion
 */
function VisualInterest (props) {
  const { data, promotionContentType } = props
  const isVideoOrSeries = promotionContentType === TYPE_CONTENT_SERIES
    || promotionContentType === TYPE_CONTENT_VIDEO
  const visualInterestField = data.getIn(['visual_interest_image', 'visual_interest_xl'], '') || ''

  if (isVideoOrSeries) {
    const image = data.get('largeKeyArtImage')
    const imageWidth = '525'
    let imageUrl = ''
    let fallbackImageUrl = ''

    try {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    } catch (e) {
      fallbackImageUrl = image
      imageUrl = image
    }

    return (
      <Picture
        fallbackSrc={fallbackImageUrl}
        width={'525'}
        height={'295'}
        alt={data.get('teaser', '')}
        className="promotion__visual-interest-picture"
        imageClassName="promotion__visual-interest-image promotion__visual-interest-image--video-series"
        loading={IMAGE_LOADING_LAZY}
        sources={[
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: imageUrl,
          },
        ]}
      />
    )
  }

  return (
    visualInterestField
      ? <img
          src={visualInterestField || ''}
          className="promotion__visual-interest-image"
          alt={data.get('teaser', '')}
        />
      : null
  )
}

/**
 * The click function for the promotion cta button
 * @param {Object} props - the props
 * @param {Map} props.data - The promotion data
 */
function handleClick (data, props, shouldSetUpstreamContext) {
  const {
    sectionId,
    sectionIndex,
    rowPlacement,
    setEventDefault,
    setUpstreamContext,
  } = props
  const eventData = getEventData(data, props)

  if (shouldSetUpstreamContext) {
    const upstreamContext = createUpstreamContext({
      screenType: SCREEN_TYPE_MEMBER_HOME,
      sectionId,
      sectionIndex,
      playEventData: eventData,
      mhTileData: {
        rowPlacement,
        rowName: data.get('title'),
        tileNumber: 0,
        clickAttribution: 'Member Home',
      },
    })

    setUpstreamContext(upstreamContext)
  }

  // GA4 event
  setEventDefault(PROMO_UNIT_CLICK_GA4_EVENT.mergeDeep(eventData))
}

/**
 * Get the event data for the promotion
 * @param {Object} data - The promotion data
 * @param {Object} props - The props
 * @returns {Object} - The event data
 */
function getEventData (data, props) {
  const {
    promotionContentId,
    promotionContentType,
    rowPlacement,
  } = props

  const title = data.get('title')
  const parentSeriesId = data.get('seriesId')
  const parentSeriesTitle = data.get('seriesTitle')
  const hasParentSeries = !!parentSeriesId && parentSeriesId > 0

  return {
    promo_name: title,
    content_id: promotionContentId,
    content_type: promotionContentType,
    content_name: title,
    row_placement: rowPlacement,
    row_name: title,
    tile_number: 0,
    click_attribution: 'Member Home',
    ...(hasParentSeries && {
      parent_series_id: parentSeriesId,
      parent_series_title: parentSeriesTitle,
    }),
  }
}

function Promotion (props) {
  const {
    language,
    promotions,
    promotionContentId,
    visibilityThreshold = 0.80,
    sectionId,
    graphQLData,
    graphQLProcessing,
    adminTitle,
    isWinbackFlowActive,
    sectionIndex,
  } = props
  let data = promotions.getIn([getSafeNodeId(promotionContentId), language, 'data'])
  let processing = promotions.getIn([getSafeNodeId(promotionContentId), language, 'processing'], true)

  if (promotionGraphQLActive) {
    data = graphQLData
    processing = graphQLProcessing
  }

  const elementRef = useRef(null)
  const [elementRefReady, setElementRefReady] = useState(false)
  const [isGaEventSent, setIsGaEventSent] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  // Winback Flow
  const isWinbackBanner = adminTitle === WINBACK_BANNER

  const setElementRef = (el) => {
    elementRef.current = el
    setElementRefReady(!!el)
  }

  // useOnScreen hook now handles useEffect internally and checks for element existence
  useOnScreen(elementRef, visibilityThreshold, setIsVisible, elementRefReady)

  useEffect(() => {
    // Only send GA data once
    if (isVisible && !isGaEventSent) {
      const { setEventDefault } = props

      // GA4 event
      const eventData = getEventData(data, props)
      setEventDefault(PROMO_UNIT_IMPRESSION_GA4_EVENT.mergeDeep(eventData))
      setIsGaEventSent(true)
    }
  }, [isVisible])

  if ((!promotionGraphQLActive && promotions.size < 1)
    || (promotionGraphQLActive && _isNil(data))
    || (isWinbackBanner && !isWinbackFlowActive)
    || processing) {
    return null
  }

  if (isWinbackBanner) {
    return (
      <PromotionWinback
        data={data}
        sectionId={sectionId}
        sectionIndex={sectionIndex}
        rowPlacement={props.rowPlacement}
        promoRef={setElementRef}
      />
    )
  }

  return (
    <div ref={setElementRef} className="promotion" data-page-manager-section-id={sectionId}>
      <Background {...props} data={data} />
      <div className="promotion__wrapper">
        <div className="promotion__container promotion__container--left">
          <Logo data={data} />
          <Description data={data} />
          <ButtonCTA {...props} data={data} />
        </div>
        <div className="promotion__container promotion__container--right">
          <VisualInterest {...props} data={data} />
        </div>
      </div>
    </div>
  )
}

Promotion.defaultProps = {
  visibilityThreshold: 0.80,
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        user,
        pmSection,
        staticText,
        optimizelyFeature,
      } = state
      const { sectionId } = props
      const promotionContentId = pmSection.getIn([sectionId, 'data', 'data', 'tabs', '0', 'tabContent', '0', 'contentId'])
      const promotionContentType = pmSection.getIn([sectionId, 'data', 'data', 'tabs', '0', 'tabContent', '0', 'contentType'])
      const language = getUserLanguagePrimary(user) || EN
      const promotions = selectPromotions(state)

      // Winback Flow
      const adminTitle = pmSection.getIn([sectionId, 'data', 'adminTitle'], '')
      const isWinbackFlowActive = isWinbackFlagEnabled(optimizelyFeature)

      return {
        adminTitle,
        isWinbackFlowActive,
        language,
        promotions,
        promotionContentId,
        promotionContentType,
        staticText: staticText.getIn(['data', 'promotion', 'data'], Map()),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(Promotion)
